import { useAuth } from '@data-delivery/okta-auth-react';
import { type I18nErrorResponseDto } from '@data-delivery/permission-ui-api';
import SplunkRum from '@splunk/otel-web';
import { QueryClientProvider } from '@tanstack/react-query';
import axios, {
  type AxiosError,
  AxiosHeaders,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import React, { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { permissionQueryClient } from '../query-client';

// Configuration surface
export interface PermissionStateProviderProps {
  children: React.ReactNode;
  shouldAttachToken?: (config: InternalAxiosRequestConfig) => boolean;
  onUnauthorized?: (error: AxiosError) => void;
  suppressToastStatuses?: number[];
  tokenCacheMs?: number;
  /** Optional override for fetching an access token. If provided, Okta hook will not be required. */
  getAccessTokenOverride?: () => Promise<string | undefined>;
  /**
   * Optional list of URL prefixes OR RegExp patterns. If provided, the Authorization header
   * will only be attached when the request URL matches at least one pattern. If omitted, all
   * requests passing shouldAttachToken() are eligible.
   */
  tokenIncludePatterns?: Array<string | RegExp>;
}

export const PermissionStateProvider: React.FC<PermissionStateProviderProps> = ({
  children,
  shouldAttachToken = () => true,
  onUnauthorized,
  suppressToastStatuses = [],
  tokenCacheMs = 500,
  getAccessTokenOverride,
  tokenIncludePatterns,
}) => {
  // Safe retrieval of auth (avoid crash if provider not yet mounted)
  let oktaGetAccessToken: (() => Promise<string | undefined>) | undefined;
  try {
    const auth = useAuth();
    oktaGetAccessToken = auth?.getAccessToken;
  } catch {
    // Swallow: will fall back to override or noop; log once in dev
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        '[PermissionStateProvider] Okta auth context not found; proceeding without token attachment.',
      );
    }
  }

  const getTokenImpl = React.useMemo(
    () => getAccessTokenOverride ?? oktaGetAccessToken ?? (async () => undefined),
    [getAccessTokenOverride, oktaGetAccessToken],
  );
  const tokenRef = useRef<{ token?: string; at: number }>({ token: undefined, at: 0 });

  // Guard to prevent multiple simultaneous unauthorized handlers
  const unauthorizedHandlerInProgress = useRef(false);

  // Storage listener (logout / token clear handling)
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key && event.key !== 'okta-token-storage') {
        return;
      }

      const token = localStorage.getItem('okta-token-storage');
      if (!token) {
        const redirect = `${window.location.protocol}//${window.location.host}/login`;
        window.location.replace(redirect);
      }
    };

    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // Axios interceptors
  useEffect(() => {
    const fetchToken = async () => {
      const now = Date.now();
      if (tokenRef.current.token && now - tokenRef.current.at < tokenCacheMs) {
        return tokenRef.current.token;
      }

      const t = await getTokenImpl();
      tokenRef.current = { token: t || undefined, at: now };
      return tokenRef.current.token;
    };

    const matchesIncludePattern = (url?: string): boolean => {
      if (!tokenIncludePatterns || tokenIncludePatterns.length === 0) {
        return true;
      } // no restriction

      if (!url) {
        return false;
      }

      return tokenIncludePatterns.some(p =>
        typeof p === 'string' ? url.startsWith(p) : p.test(url),
      );
    };

    const requestId = axios.interceptors.request.use(async config => {
      if (!shouldAttachToken(config)) {
        return config;
      }

      if (!matchesIncludePattern(config.url)) {
        return config;
      }

      try {
        const token = await fetchToken();
        if (token) {
          if (!(config.headers instanceof AxiosHeaders)) {
            config.headers = new AxiosHeaders(config.headers ?? undefined);
          }

          (config.headers as AxiosHeaders).set('Authorization', `Bearer ${token}`);
        }
      } catch {
        /* silent */
      }

      return config;
    });

    const responseId = axios.interceptors.response.use(
      (res: AxiosResponse) => res,
      (error: AxiosError<I18nErrorResponseDto>) => {
        SplunkRum.error(error);

        if (error.response?.status === 401) {
          // Prevent multiple concurrent unauthorized handlers from running
          if (unauthorizedHandlerInProgress.current) {
            return Promise.reject(error);
          }

          // Skip handler if we're already on the not-authorized page
          if (window.location.pathname === '/not-authorized') {
            return Promise.reject(error);
          }

          unauthorizedHandlerInProgress.current = true;

          // Check if this is the "no roles" unauthorized error
          const data = error.response?.data as unknown as {
            errorCode?: number;
            errorMessage?: string;
            i18nMessage?: string;
            metadata?: Record<string, unknown> & { reason?: string; email?: string };
            reason?: string;
          };

          // Check metadata.reason for our custom "no roles" message
          const customReason = data?.metadata?.reason || '';

          // Detect "no roles" error by checking if metadata.reason contains our specific message
          const isNoRolesError =
            data?.i18nMessage === 'exceptions.unauthorized' &&
            customReason.toLowerCase().includes('not authorized to access this system');

          if (isNoRolesError) {
            // Store the custom reason message for display on the page
            sessionStorage.setItem('authErrorMessage', customReason);

            // Logout the user before redirecting
            onUnauthorized?.(error);

            // Redirect to not-authorized page after logout
            setTimeout(() => {
              window.location.href = '/not-authorized';
              // Reset flag after redirect
              setTimeout(() => {
                unauthorizedHandlerInProgress.current = false;
              }, 500);
            }, 100);

            return Promise.reject(error);
          }

          // For other 401 errors, call the unauthorized handler (e.g., logout)
          onUnauthorized?.(error);

          // Reset flag after a short delay to allow handler to complete
          setTimeout(() => {
            unauthorizedHandlerInProgress.current = false;
          }, 1000);
        }

        const status = error.response?.status;
        if (!(status && suppressToastStatuses.includes(status))) {
          if (status !== 401) {
            const data = error.response?.data;
            if (data?.errorMessage) {
              toast.error(data.errorCode, {
                description: data.errorMessage,
              });
              error.message = data.errorMessage;
            } else {
              toast.error(error.message, {
                description: 'Please ensure if you are connected with VPN',
              });
            }
          }
        }

        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.request.eject(requestId);
      axios.interceptors.response.eject(responseId);
      // Reset the flag on cleanup
      unauthorizedHandlerInProgress.current = false;
    };
  }, [
    getTokenImpl,
    onUnauthorized,
    shouldAttachToken,
    suppressToastStatuses,
    tokenCacheMs,
    tokenIncludePatterns,
  ]);

  return <QueryClientProvider client={permissionQueryClient}>{children}</QueryClientProvider>;
};
